<script setup>
import { useI18n } from 'vue-i18n';
import Icon from 'next/icon/Icon.vue';
import Label from 'dashboard/components-next/label/Label.vue';

defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    default: '',
  },
  icon: {
    type: String,
    required: true,
  },
  compact: {
    type: Boolean,
    default: false,
  },
  isComingSoon: {
    type: Boolean,
    default: false,
  },
  isBeta: {
    type: Boolean,
    default: false,
  },
  hasVoiceBadge: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
</script>

<template>
  <button
    type="button"
    class="relative min-w-0 bg-n-surface-1 gap-4 cursor-pointer rounded-lg flex justify-start transition-colors p-5 border border-solid border-n-weak focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-blue-8"
    :class="{
      'flex-row items-center': compact,
      'flex-col items-start': !compact,
      'hover:enabled:border-n-blue-8 hover:enabled:bg-n-blue-2 disabled:opacity-60 disabled:cursor-not-allowed':
        !isComingSoon,
      'cursor-not-allowed disabled:opacity-80': isComingSoon,
    }"
  >
    <div class="relative shrink-0">
      <div
        class="flex size-10 items-center justify-center rounded-md bg-n-blue-3"
      >
        <Icon :icon="icon" class="text-n-blue-11 size-5" />
      </div>
      <div
        v-if="hasVoiceBadge"
        class="absolute -top-1 ltr:-right-1 rtl:-left-1 flex size-4 items-center justify-center rounded-full bg-n-alpha-2 ring-2 ring-n-solid-1"
      >
        <Icon icon="i-lucide-audio-lines" class="text-n-slate-10 size-2.5" />
      </div>
    </div>

    <div class="flex min-w-0 flex-col items-start gap-1.5">
      <div class="flex flex-wrap items-center gap-2">
        <h3 class="mb-0 text-n-slate-12 text-sm text-start font-semibold">
          {{ title }}
        </h3>
        <Label
          v-if="isBeta && !isComingSoon"
          v-tooltip.top="t('GENERAL.BETA_DESCRIPTION')"
          :label="t('GENERAL.BETA')"
          color="blue"
          compact
        />
      </div>
      <p class="mb-0 text-n-slate-11 text-start text-sm leading-6">
        {{ description }}
      </p>
    </div>

    <div v-if="isComingSoon" class="mt-auto rounded bg-n-slate-3 px-2 py-1">
      <span class="text-n-slate-11 font-medium text-xs">
        {{ t('CHANNEL_SELECTOR.COMING_SOON') }}
      </span>
    </div>
  </button>
</template>
